import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { CircularProgress, FormControl, FormControlLabel, IconButton, Modal, Paper, Radio, RadioGroup, Typography } from "@mui/material"
import { Fragment, ReactNode, useEffect, useRef, useState } from "react"
import { parseFalsyFields } from '../utils/ValidationUtils';
import { computeBillingCycleEnd, billingCycleDisplayLabel } from '../utils/CommonUtils';
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { BillingCycle, BookingType, OrderMode, OrderModel } from "../utils/Models";
import { Box } from "@mui/system";
import { DateTime } from "luxon";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";

// Pluralized "N <unit>" label for a single cycle - FIFTEEN_DAYS isn't a single noun, so it gets "N x 15 Days" instead
const cycleUnitLabel: Record<BillingCycle, string> = {
  [BillingCycle.DAILY]: 'day',
  [BillingCycle.WEEKLY]: 'week',
  [BillingCycle.FIFTEEN_DAYS]: '',
  [BillingCycle.MONTHLY]: 'month',
  [BillingCycle.QUARTERLY]: 'quarter'
};
function cycleCountLabel(cycle: BillingCycle, n: number): string {
  if (cycle === BillingCycle.FIFTEEN_DAYS) return `${n} x 15 Days`;
  const unit = cycleUnitLabel[cycle];
  return `${n} ${unit}${n > 1 ? 's' : ''}`;
}
const billingCycleOptions = (currentCycle?: BillingCycle) => Object.values(BillingCycle).map(c => ({
  value: c,
  display: c === currentCycle ? `${billingCycleDisplayLabel[c]} (current)` : billingCycleDisplayLabel[c]
}));
const cycleCountOptions = (cycle: BillingCycle) => Array.from({ length: 12 }, (_, i) => i + 1).map(n => ({
  value: n,
  display: cycleCountLabel(cycle, n)
}));

// Interfaces and constants to store state
interface LocalState {
  loading?: boolean;
  error?: string;
  extendData?: {
    extensionPrice: number;
  },
  formEnabled?: boolean
}
interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
}
// const paymentMethods = [
//   { value: 1, string: 'Send payment link to the customer', key: 'ONLINE' },
//   { value: 2, string: 'Collected cash from customer', key: 'OFFLINE' }
// ]

// Start booking component
export function ExtendBooking(props: {
  children: ReactNode,
  order: OrderModel,
  style?: any,
  backFn: () => void
}) {

  // Refs
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // Subscription orders extend in multiples of a billing cycle instead of free-form days/hours
  const isSubscription = props.order.bookingType === BookingType.SUBSCRIPTION && Boolean(props.order.billingCycle);

  // Local state
  const [newEndDate, setNewEndDate] = useState<DateTime>(
    isSubscription
      ? DateTime.fromMillis(computeBillingCycleEnd(Number(props.order.rideEndScheduled), props.order.billingCycle as BillingCycle))
      : DateTime.fromMillis(Number(props.order.rideEndScheduled)).plus({ days: 1 })
  );
  const [durationState, setDurationState] = useState<{ days: number, hours: number, discount: number }>({ days: 1, hours: 0, discount: props.order.lastExtensionDiscount || 0 });
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle | undefined>(props.order.billingCycle);
  const [cycleCount, setCycleCount] = useState<number>(1);
  // Subscription orders default to requiring payment before the extension applies - dealer must explicitly choose offline collection
  const [paymentMethod, setPaymentMethod] = useState<'ONLINE' | 'OFFLINE'>('ONLINE');
  const [state, setState] = useState<LocalState>({});
  const [formState, setFormState] = useState<FormState>({});
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setSelectedCycle(props.order.billingCycle);
    setCycleCount(1);
    setNewEndDate(
      isSubscription
        ? DateTime.fromMillis(computeBillingCycleEnd(Number(props.order.rideEndScheduled), props.order.billingCycle as BillingCycle))
        : DateTime.fromMillis(Number(props.order.rideEndScheduled)).plus({ days: 1 })
    );
    setDurationState({ days: 1, hours: 0, discount: props.order.lastExtensionDiscount || 0 });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // Parse state or props
  const authCookie = getAuthCookie();
  const extendPrice = (state.extendData && state.extendData.extensionPrice >= 0) ? state.extendData.extensionPrice : -1;

  // Load data on when component opens
  async function fetchPrice(discount?: number) {
    if (!open || !newEndDate) return;
    // setState({ loading: true });
    (async () => {
      try {
        if (!props.order.bikes || !props.order.bikes.length || !props.order.bikes[0].id) {
          throw new Error('Failed to load bike data!');
        }
        const { data } = await GobikesApi.get(`/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/extend-price?newEndDate=${newEndDate.toMillis()}&vendorDiscount=${discount !== undefined ? discount : durationState.discount}`, { headers: { 'vendor-token': authCookie } });
        setState({ loading: false, extendData: data.data, formEnabled: true });
      } catch (e: any) {
        setState({ loading: false, error: e?.response?.data?.message || e.message });
      }
    })();
  }
  useEffect(() => { fetchPrice() }, [props, open, newEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute the new end date for a subscription extension: current end date + count x selected cycle
  function computeSubscriptionEndDate(cycle: BillingCycle, count: number): DateTime {
    let cursor = Number(props.order.rideEndScheduled);
    for (let i = 0; i < count; i++) cursor = computeBillingCycleEnd(cursor, cycle);
    return DateTime.fromMillis(cursor);
  }

  // Handle  form submission
  async function submitExtendOrderRequest(e: any) {

    // Parse input and check for errors
    e.preventDefault();
    const invalidFields = parseFalsyFields([
      { name: 'End date', value: newEndDate }
      // { name: 'Payment method', value: paymentMethod }
    ]);
    if (invalidFields.length) {
      setFormState({ error: true, message: `Please enter these required fields: ${invalidFields.join(', ')}`, loading: false });
      return;
    } else {
      setFormState({ error: false })
    }
    const isCycleChanged = isSubscription && selectedCycle && selectedCycle !== props.order.billingCycle;
    const payload = {
      newEndDate: newEndDate.toMillis(),
      ...(durationState.discount && durationState.discount > 0 && { vendorDiscount: durationState.discount }),
      ...(isCycleChanged && { billingCycle: selectedCycle, overrideMultipleCheck: true }),
      ...(isSubscription && { collectOffline: paymentMethod === 'OFFLINE' })
    };

    // Set loading mode and try to extend booking
    setFormState({ loading: true });
    try {
      const { data } = await GobikesApi.put(`/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/extend`, payload, { headers: { 'vendor-token': authCookie } });
      if (data) {
        setFormState({ error: false, message: data.data.plGenerated ? 'Success! Sent payment link to customer.' : 'Booking extended!', loading: false })
        setTimeout(() => { props.backFn() }, 2000);
      } else {
        setFormState({ error: true, message: 'An unknown error occured', loading: false })
      }
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setFormState({ error: true, message: errorMessage, loading: false })
    }
  }

  // Return JSX
  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>
      <Modal
        open={open}
        className="gbk-modal"
        onClose={handleClose}
      >
        <Box className="gbk-modal-content">
          {state.loading ? renderLoading() : renderExtendForm()}
        </Box>
      </Modal >
    </Fragment>
  )

  // Render loading
  function renderLoading() {
    return <Paper className='gbk-paper'>
      <div className="loader">
        {state.loading ? <CircularProgress /> : ''}
        <div className={`gbk-message ${state.error ? 'error' : ''}`}>
          {state.error}
        </div>
      </div>
    </Paper>
  }

  // Render start order form
  function renderExtendForm() {
    return <Paper className='gbk-paper'>

      {/* Header */}
      <div className="header">
        <Typography className="header-left"><b>Extend Booking</b></Typography>
        <IconButton className="header-right" onClick={handleClose} style={{ padding: 0 }}>
          <Close />
        </IconButton>
      </div>

      {/* Forms */}
      <div className="content">

        <FormControl sx={{ margin: { xs: '10px 16px', md: '20px 30px' }, width: { xs: '92%', md: '80%' } }} >
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={`Current end date`}
            editMode={false}
            value={props.order.rideEndScheduled || null}
          />
          {isSubscription ? (
            <Fragment>
              <GobikesField
                gbkFieldSx={{ marginTop: { xs: '4px', md: '0px' } }}
                type={GbkFieldType.SELECT}
                label={`Billing Cycle`}
                editMode={true}
                selectOptions={billingCycleOptions(props.order.billingCycle)}
                value={selectedCycle}
                updateFn={(cycle: BillingCycle) => {
                  setSelectedCycle(cycle);
                  setCycleCount(1);
                  setNewEndDate(computeSubscriptionEndDate(cycle, 1));
                }}
              />
              <GobikesField
                gbkFieldSx={{ marginTop: { xs: '4px', md: '10px' } }}
                type={GbkFieldType.SELECT}
                label={`Extend by`}
                editMode={true}
                selectOptions={cycleCountOptions(selectedCycle || BillingCycle.WEEKLY)}
                value={cycleCount}
                updateFn={(count: number) => {
                  setCycleCount(count);
                  setNewEndDate(computeSubscriptionEndDate(selectedCycle || BillingCycle.WEEKLY, count));
                }}
              />
            </Fragment>
          ) : (
            <Fragment>
              <GobikesField
                gbkFieldSx={{ marginTop: { xs: '4px', md: '0px' } }}
                type={GbkFieldType.NUMBER}
                label={`Days to extend`}
                editMode={true}
                value={durationState.days}
                updateFn={(days) => {
                  const newDurationState = { ...durationState, days };
                  setDurationState(newDurationState)
                  const newEndDate = DateTime.fromMillis(Number(props.order.rideEndScheduled))
                    .plus({ days: newDurationState.days, hours: newDurationState.hours });
                  setNewEndDate(newEndDate);
                }}
              />
              <GobikesField
                gbkFieldSx={{ marginTop: { xs: '4px', md: '-20px' } }}
                type={GbkFieldType.NUMBER}
                label={`Hours to extend`}
                editMode={true}
                value={durationState.hours}
                updateFn={(hours) => {
                  const newDurationState = { ...durationState, hours };
                  setDurationState(newDurationState)
                  const newEndDate = DateTime.fromMillis(Number(props.order.rideEndScheduled))
                    .plus({ days: newDurationState.days, hours: newDurationState.hours });
                  setNewEndDate(newEndDate);
                }}
              />
            </Fragment>
          )}
          <GobikesField
            gbkFieldSx={{ marginTop: { xs: '4px', md: isSubscription ? '10px' : '-20px' } }}
            type={GbkFieldType.NUMBER}
            label={`Discount (₹)`}
            editMode={true}
            value={durationState.discount}
            updateFn={(discount) => {
              const newDurationState = { ...durationState, discount };
              setDurationState(newDurationState)
              fetchPrice(discount);
            }}
          />
          <GobikesField
            gbkFieldSx={{ marginTop: { xs: '4px', md: '-14px' } }}
            type={GbkFieldType.DATE_TIME}
            label={`New end date`}
            editMode={false}
            value={newEndDate || null}
          />
          <GobikesField
            gbkFieldSx={{ marginTop: { xs: '4px', md: '0px' } }}
            type={GbkFieldType.STRING}
            label={`Total extension`}
            editMode={false}
            value={isSubscription
              ? `${cycleCountLabel(selectedCycle || BillingCycle.WEEKLY, cycleCount)}${selectedCycle !== props.order.billingCycle ? ' (billing cycle changed)' : ''}`
              : `${durationState.days} days, ${durationState.hours} hours`}
          />
          <div className={`gbk-message ${state.error ? 'error' : ''}`} style={{ paddingTop: 5 }}>
            {state.error}
          </div>
        </FormControl>

        {/* Payment method - subscription orders must pay before the extension applies, unless the dealer confirms cash was collected offline */}
        {isSubscription && extendPrice > Number.EPSILON && (
          <FormControl sx={{ margin: { xs: '0px 16px', md: '0px 30px' }, width: { xs: '92%', md: '80%' } }}>
            <Typography sx={{ fontSize: '13px', color: '#666', mb: '4px' }}>Payment Method</Typography>
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as 'ONLINE' | 'OFFLINE')}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
            >
              <FormControlLabel value="ONLINE" control={<Radio size="small" />} label="Send Payment Link" />
              <FormControlLabel value="OFFLINE" control={<Radio size="small" />} label="Cash Collected Offline" />
            </RadioGroup>
          </FormControl>
        )}

        {/* Extend price details */}
        <Box sx={{ margin: { xs: '0px 16px', md: '0px 30px' }, width: { xs: '92%', md: '80%' } }}>
          <Typography fontSize={{ xs: 14, md: 18 }} fontWeight={500} paddingTop={1}>Payment Details</Typography>
          <ul className="gbk-joined-list">
            <li>
              <div className="float-left">Extra Amount to be paid</div>
              <div className="float-right">{extendPrice >= 0 ? <span className="roboto">{`₹ ${extendPrice.toFixed(2)}`}</span> : '-'}</div>
            </li>
          </ul>
          <br />
        </Box>

        {/* Payment method */}
        <FormControl sx={{ margin: { xs: '0px 16px', md: '0px 30px' }, width: { xs: '92%', md: '80%' } }} >
          {/* Message */}
          <div className={`gbk-login-message ${formState.error ? 'error' : ''}`}>{formState.message}</div>
        </FormControl>

      </div>

      {/* Footer */}
      <div className="footer">
        <form onSubmit={submitExtendOrderRequest}>
          <LoadingButton
            variant='contained'
            type='submit'
            ref={submitButtonRef}
            loading={formState.loading}
            className="no-text-transform"
            sx={{ width: 'calc(100% - 20px)', ml: '10px' }}
            disabled={!state.formEnabled}
          >
            {extendPrice > Number.EPSILON ? (
              isSubscription
                ? (paymentMethod === 'OFFLINE' ? 'Extend Now' : 'Send Payment Link for Extension')
                : (props.order.orderMode === OrderMode.VENDOR ? 'Extend Now' : 'Send Payment Link for Extension')
            ) : 'Extend booking'}
          </LoadingButton>
        </form>
      </div>
    </Paper >
  }

}

